// The period a window panel (P5, P11, P14, P16) covers, read from its snapshot's own
// `period_start` / `period_end` so the panel says what it counts instead of leaving it to the
// reader to guess from the run picker.
//
// WARNING: never route these dates through `new Date(...)`, `toLocale*String` or any other Date
// formatting. A bare `YYYY-MM-DD` is read as UTC midnight, which is the previous evening west of
// UTC, so the label would name the wrong day for viewers in the Americas. The test pins only that
// no date string is parsed, not what a later formatting step would do.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Read the calendar day out of a `YYYY-MM-DD` date (or a timestamp that starts with one).
 *
 * Parsed by parts, never through `new Date(string)`: that reads a bare date as UTC midnight, which
 * is the evening before in any timezone west of UTC and would shift the label a day.
 *
 * @param {string|null|undefined} value
 * @returns {{year: number, month: number, day: number}|null} `month` is 1-12; null if unreadable
 */
function parseDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * Label the period a snapshot covers, e.g. "30 days to Oct 5".
 *
 * Days are the whole days between the two dates. With no usable start the label is just the end
 * ("to Oct 5"); with no end there is nothing to say.
 *
 * @param {{period_start?: string|null, period_end?: string|null}|null|undefined} snapshot
 * @returns {string|null} null when `period_end` is missing or unreadable
 */
export function windowPeriodLabel(snapshot) {
  const end = parseDay(snapshot?.period_end);
  if (!end) return null;
  const endText = `${MONTHS[end.month - 1]} ${end.day}`;
  const start = parseDay(snapshot?.period_start);
  if (!start) return `to ${endText}`;
  const days = Math.round(
    (Date.UTC(end.year, end.month - 1, end.day) - Date.UTC(start.year, start.month - 1, start.day)) / 86400000,
  );
  if (days < 0) return `to ${endText}`;
  return `${days} day${days === 1 ? '' : 's'} to ${endText}`;
}
